import { UserOrEmailMapping } from "../typings/admin.types";

/** Lowercase trimmed name part used only to order and match department users. */
const namePart = (value: string | undefined): string => value?.trim().toLowerCase() ?? "";

/**
 * Missing names sort last. Named parts use German dictionary order.
 */
const compareNamePart = (left: string, right: string): number => {
  if (left === "" && right === "") {
    return 0;
  }
  if (left === "") {
    return 1;
  }
  if (right === "") {
    return -1;
  }
  return left.localeCompare(right, "de");
};

/**
 * Surname, then first name, then email.
 * Used for the department-user chips and the suggestion list.
 */
export const compareDepartmentUsers = (a: UserOrEmailMapping, b: UserOrEmailMapping): number => {
  const bySurname = compareNamePart(namePart(a.surname), namePart(b.surname));
  if (bySurname !== 0) {
    return bySurname;
  }
  const byFirstName = compareNamePart(namePart(a.firstName), namePart(b.firstName));
  if (byFirstName !== 0) {
    return byFirstName;
  }
  return (a.email ?? "").localeCompare(b.email ?? "", "de");
};

/** Identity keys for one person: user id and email, so repeats collapse. */
const departmentUserIdentityKeys = (user: UserOrEmailMapping): string[] => {
  const userId = user.userId?.trim();
  const email = user.email?.trim().toLowerCase();
  return [userId ? `id:${userId}` : "", email ? `email:${email}` : ""].filter(
    key => key.length > 0
  );
};

/** True when both rows are the same person by user id or email. */
export const sameDepartmentUser = (
  left: UserOrEmailMapping,
  right: UserOrEmailMapping
): boolean => {
  const rightKeys = new Set(departmentUserIdentityKeys(right));
  return departmentUserIdentityKeys(left).some(key => rightKeys.has(key));
};

/** Identity keys for everyone already in the department. */
const assignedIdentityKeys = (assigned: UserOrEmailMapping[]): Set<string> =>
  new Set(assigned.flatMap(departmentUserIdentityKeys));

/** True when this colleague is already in the department. */
const isAssignedDepartmentUser = (option: UserOrEmailMapping, assignedKeys: Set<string>): boolean =>
  departmentUserIdentityKeys(option).some(key => assignedKeys.has(key));

/**
 * Colleagues who can still be added. People already in the department stay in the roster.
 */
export const omitAssignedDepartmentUsers = (
  options: UserOrEmailMapping[],
  assigned: UserOrEmailMapping[]
): UserOrEmailMapping[] => {
  const assignedKeys = assignedIdentityKeys(assigned);
  return options.filter(option => !isAssignedDepartmentUser(option, assignedKeys));
};

/**
 * Suggestion order. People already in the department come first, then the others.
 * Each group keeps the surname order of the list passed in.
 */
export const orderDepartmentUserSuggestions = (
  options: UserOrEmailMapping[],
  assigned: UserOrEmailMapping[]
): UserOrEmailMapping[] => {
  const assignedKeys = assignedIdentityKeys(assigned);
  const selected: UserOrEmailMapping[] = [];
  const available: UserOrEmailMapping[] = [];
  for (const option of options) {
    if (isAssignedDepartmentUser(option, assignedKeys)) {
      selected.push(option);
    } else {
      available.push(option);
    }
  }
  return [...selected, ...available];
};

/**
 * One row per person. Repeated user ids or emails from the all-users payload
 * were showing the same colleague several times in the suggestion list.
 */
export const dedupeDepartmentUsers = (users: UserOrEmailMapping[]): UserOrEmailMapping[] => {
  const seen = new Set<string>();
  const unique: UserOrEmailMapping[] = [];
  for (const user of users) {
    const keys = departmentUserIdentityKeys(user);
    if (keys.some(key => seen.has(key))) {
      continue;
    }
    for (const key of keys) {
      seen.add(key);
    }
    unique.push(user);
  }
  return unique;
};

/** True when the token matches the mailbox name, not the company domain. */
const emailLocalMatches = (email: string | undefined, token: string): boolean => {
  if (!email) {
    return false;
  }
  const lower = email.toLowerCase();
  if (token.includes("@")) {
    return lower.includes(token);
  }
  const local = lower.split("@")[0];
  if (local.startsWith(token)) {
    return true;
  }
  const segments = local.split(/[._+\-]+/).filter(segment => segment.length > 0);
  return segments.some(segment => segment.startsWith(token));
};

/** True when the token appears in the person's first name or surname. */
const nameMatches = (user: UserOrEmailMapping, token: string): boolean => {
  const name = [user.firstName, user.surname]
    .map(part => namePart(part))
    .filter(part => part.length > 0)
    .join(" ");
  return name.includes(token);
};

/**
 * Suggestions for the department-user field.
 * Every typed word must match the name or the email local-part.
 * A shared company domain such as recotech.de does not match everyone.
 */
export const filterDepartmentUserOptions = (
  options: UserOrEmailMapping[],
  inputValue: string
): UserOrEmailMapping[] => {
  const unique = dedupeDepartmentUsers(options).sort(compareDepartmentUsers);
  const tokens = inputValue
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map(token => token.replaceAll(/[^a-z0-9äöüß.@+-]/gi, ""))
    .filter(token => token.length > 0);
  if (tokens.length === 0) {
    return unique;
  }
  return unique.filter(user =>
    tokens.every(token => nameMatches(user, token) || emailLocalMatches(user.email, token))
  );
};
